import classNames from "classnames";
import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useDispatch, batch } from "react-redux";
import { useParams } from 'react-router';

import {
	saveWorkingSession,
	setOnDemandVideo,
	setSessionBroadcastType,
	updateWorkingSession,
} from "../../../../../store/actions/admin/create-event/session";
import { SessionTypesEnum, EBroadcastTypes, StaticVideo, VideoType } from "../../../../../types/working-model";
import CustomHTML from "../../../../general-ui/edit-html/edit-html";
import Icon, { ICONS, COLORS } from "../../../../general-ui/icon";
import Modal from "../../../../general-ui/modal/modal";
import images from "../../../../../images";
import { ParamsProps } from '../../../../live-event/live-event';
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { setADropdownIsOpen, toggleADropdownIsOpen } from "../../../../../store/actions/admin/create-event";
import { OptionalComponent } from "utils/optional-component";
import { useCanEnableMeetFiresides } from "hooks/channel.hooks";
import { Case, DefaultCase, SwitchCase } from "@general-ui/switch-case/switch-case";
import { GetDefaultNewStaticVideo } from "store/utils/create-event";
import { showAlert } from "@general-ui/alert/alert-service";
import { UploadedVideo } from "connection/videos";
import { useSessionContext } from "contexts/session-context";

interface IVideoModal {
	onClose: () => void;
}

const BroadcastStreamingModal: React.FC<IVideoModal> = ({ onClose}: IVideoModal) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);

	const dispatch = useDispatch();
	const { language } = useParams<ParamsProps>();
	const [workingOnDemandVideo, setWorkingOnDemandVideo] = useState<StaticVideo | null>(null);
	const [selectedFiresideType, setSelectedFiresideType] = useState<"meet" | "native">(workingSession?.fireside_session_settings?.settings?.use_google_meet ? "meet" : "native");
	const [selectingType, setSelectingType] = useState(false);
	const videoTypeSelectRef = useRef(null);
	const canEnableMeet = useCanEnableMeetFiresides();
	const { onDemandVideo: staticVideo } = useSessionContext();

	const meetFireside = selectedFiresideType === 'meet';
	// because we actually update the database when a type is selected instead of when we click "done", we need to store the
	// original value somewhere, so if a user clicks "cancel" instead of "done", we can reset it back to the original.
	// The best way to handle this would be to re-write the entire component so that we store a temp local state when a user selects
	// and we only update the database when the user clicks "done", but this was updated during a bugbash and that's too dangerous of a lift
	// to push to staging
	const originalBroadcastType = useRef(workingSession?.broadcast_type);

	useEffect(() => {
		// Since we save, we only want to set the embed video if it's embed. We don't want to update a recored/uploaded video
		if (staticVideo && staticVideo.type === VideoType.embed) {
			setWorkingOnDemandVideo(staticVideo);
		} else {
			setWorkingOnDemandVideo({
				...GetDefaultNewStaticVideo(),
				type: VideoType.embed,
			});
		}

	}, [language, staticVideo]);

	useEffect(() => {
		function checkClick(e: MouseEvent) {
			const path = e.composedPath();
			for (const item of path) {
				if (videoTypeSelectRef.current === item) { return; }
			}
			setSelectingType(false);
			dispatch(setADropdownIsOpen(false));
		}

		if (selectingType) {
			window.addEventListener('click', checkClick);
		}

		return () => window.removeEventListener('click', checkClick);
	}, [dispatch, selectingType, setSelectingType]);

	function setSelectedType(type:  "meet" | "native") {
		if (!workingSession) {
			return;
		}


		setSelectedFiresideType(type);

		setSelectingType(false);
		dispatch(setADropdownIsOpen(false));
	}


	function setSelectedBroadcastType(type: EBroadcastTypes) {
		return () => {
			if (type === EBroadcastTypes.embed && workingSession?.layout.overlayChat) {
				// embeded video can not have overlay chat
				dispatch(updateWorkingSession({
					...workingSession,
					layout: {
						...workingSession?.layout,
						overlayChat: false
					}
				}));
			}
			dispatch(setSessionBroadcastType(type));
			setSelectingType(false);
			dispatch(setADropdownIsOpen(false));
		};
	}


	const handleVideoEmbed = useCallback((code: string) => {
		setWorkingOnDemandVideo(workingOnDemandVideo => {
			if (!workingOnDemandVideo) return null;

			return {
				...workingOnDemandVideo,
				source: code,
				session_language: language
			};
		});
	}, [language]);

	const renderVideoSelect = useMemo(() => {
			switch (workingSession?.broadcast_type) {
				case EBroadcastTypes.greenroom: {
					// greenroom creation for broadcast session streaming happens in greenroom.
					// getGreenroomBroadcastSessionsByChannel in events-api pulls session data into greenroom to populate the list,
					// but the greenroom isn't actually created until the producer in greenroom creates it
					return null;
				}
				case EBroadcastTypes.rtmps: {
					return null; // moved rtmp info into panel
				}
				case EBroadcastTypes.embed: {
					return (
						<div className="video-embed-code">
							<label>Embed Code</label>
							<CustomHTML
								onChange={handleVideoEmbed}
								defaultValue={workingOnDemandVideo?.source ?? ''}
							/>
						</div>
					);
				}
				case EBroadcastTypes.fireside: {
					return null;
				}
				default: {
					return <h3>Nothing selected</h3>;
				}
			}

	}, [workingSession?.broadcast_type, handleVideoEmbed, workingOnDemandVideo?.source]);

	const selectedWorkingType = useMemo(() => {
		if (workingSession?.session_type === SessionTypesEnum.broadcast) {
			switch (workingSession?.broadcast_type) {
				case EBroadcastTypes.greenroom: {
					return <GreenroomStreaming />;
				}
				case EBroadcastTypes.rtmps: {
					return <RTMPStreaming />;
				}
				case EBroadcastTypes.embed: {
					return <EmbedIframe />;
				}
				default: return <></>;
			}
		} else if (workingSession?.session_type === SessionTypesEnum.fireside) {
			if (canEnableMeet && meetFireside) {
				return <MeetFiresides />;
			} else {
				return <NativeFiresides />;
			}
		}

	}, [workingSession?.session_type, workingSession?.broadcast_type, canEnableMeet, meetFireside]);

	function close(resetOriginalBroadcastType?: boolean) {
		// resetOriginalBroadcastType is only passed in when the modal is cancelled, not when "done" is clicked
		// this way we can reset the original value
		if (resetOriginalBroadcastType && originalBroadcastType?.current && workingSession?.session_type === SessionTypesEnum.broadcast) {
			setSelectedBroadcastType(originalBroadcastType.current)();
		}

		onClose();
	}

	const title = () => {
		switch (workingSession?.session_type) {
			case SessionTypesEnum.onDemand:
				return "On-demand Session Stream Settings";
			case SessionTypesEnum.broadcast:
				return "Broadcast Session Stream Settings";
			case SessionTypesEnum.fireside:
				return "Fireside Session Stream Settings";
			default:
				return "Session Stream Settings";
		}
	};

	const handleSave = async () => {
		if (!token || !user) {
			return;
		}

		// Only update and save the video if the type is embed
		if (workingSession?.broadcast_type === EBroadcastTypes.embed) {
			const video = workingOnDemandVideo;

			if (video) {
				const lang = workingSession.streaming_options.single_stream ? workingSession.default_language : language;
				if (video.base_language === undefined) {
					video.base_language = lang;
				}
				try {
					const savedVideo = await UploadedVideo(token, video, user.active_channel);
					dispatch(setOnDemandVideo(savedVideo, lang));
				} catch (e) {
					console.error(e);
					showAlert({
						type: "error",
						message: "Error saving video",
					});
				}
			}
		}

		if (workingSession?.session_type === SessionTypesEnum.fireside) {
			dispatch(updateWorkingSession({
				...workingSession,
				fireside_session_settings: workingSession.fireside_session_settings ? {
					...workingSession.fireside_session_settings,
					settings: {
						...workingSession.fireside_session_settings?.settings,
						use_google_meet: selectedFiresideType === "meet"
					}
				} : undefined
			}));
			dispatch(saveWorkingSession());
		}
		
		close();
	};

	return (
		<Modal
			open={true}
			onRequestClose={() => close(true)}
			title={title()}
			cancellable={true}
			closeable={false}
			padTitle={true}
			footer={(
				<>
					<button onClick={() => close(true)} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={handleSave} className="lemonade">Done</button>
				</>
			)}
		>
			<div className="add-video-modal">
				<div className="video-type-select" ref={videoTypeSelectRef}>
					<div className={classNames('video-type-selected', 'select-streaming-type', { open: selectingType })} onClick={() => { setSelectingType(!selectingType); dispatch(toggleADropdownIsOpen()); }}>
						{selectedWorkingType}
						<span className="arrow">
							<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={24} color={COLORS.WHITE} />
						</span>
					</div>
					<div className={classNames("video-type-selector", { open: selectingType })}>
						<SwitchCase input={workingSession?.session_type}>
							<Case value={SessionTypesEnum.broadcast}>
								<>
									<OptionalComponent display={workingSession?.broadcast_type !== EBroadcastTypes.greenroom}>
										<div className="select-option" onClick={setSelectedBroadcastType(EBroadcastTypes.greenroom)}>
											<GreenroomStreaming />
										</div>
									</OptionalComponent>
									<OptionalComponent display={workingSession?.broadcast_type !== EBroadcastTypes.rtmps}>
										<div className="select-option" onClick={setSelectedBroadcastType(EBroadcastTypes.rtmps)}>
											<RTMPStreaming />
										</div>
									</OptionalComponent>
									<OptionalComponent display={workingSession?.broadcast_type !== EBroadcastTypes.embed}>
										<div className="select-option" onClick={setSelectedBroadcastType(EBroadcastTypes.embed)}>
											<EmbedIframe />
										</div>
									</OptionalComponent>
								</>
							</Case>
							<Case value={SessionTypesEnum.fireside}>
								<>
									<OptionalComponent display={canEnableMeet && !meetFireside}>
										<div className="select-option" onClick={() => setSelectedType('meet')}>
											<MeetFiresides />
										</div>
									</OptionalComponent>
									<OptionalComponent display={!canEnableMeet || meetFireside}>
										<div className="select-option" onClick={() => setSelectedType('native')}>
											<NativeFiresides />
										</div>
									</OptionalComponent>
								</>
							</Case>
							<DefaultCase>
								<div className="select-option">
									<label>No options</label>
								</div>
							</DefaultCase>
						</SwitchCase>
					</div>
				</div>

				{renderVideoSelect}
			</div>
		</Modal>
	);
};

const EmbedIframe = () => (
	<>
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CODE} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Embedded Iframe</label>
				<span>Copy & paste the code of embed HTML code</span>
			</div>
		</div>
	</>
);

function GreenroomStreaming() {
	return (
		<div className="video-selected-type">
			<div className="icon-greenroom">
				<img src={images.GreenroomLogo} alt="greenroom" />
			</div>
			<div className="video-selected-info">
				<label>Greenroom Streaming</label>
				<span>Stream directly from Greenroom</span>
			</div>
		</div>
	);
}

function RTMPStreaming() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CAMERA_ON} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>RTMP streaming</label>
				<span>Stream from the software of your choice</span>
			</div>
		</div>
	);
}

function MeetFiresides() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CAMERA_ON} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Google Meet</label>
				<span>Use Google Meet as your video delivery for your session</span>
			</div>
		</div>
	);
}

function NativeFiresides() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CAMERA_ON} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Native Firesides</label>
				<span>Use Brandlive's fireside stage as your video delivery for your session</span>
			</div>
		</div>
	);
}

export default BroadcastStreamingModal;
